import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, X, Globe, Link as LinkIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft, Check, RefreshCw, Plus, X as XIcon, GripVertical as GripIcon, Globe as GlobeIcon, Mail, Phone, MapPin,
  Briefcase, User, Link as LinkIcon2, Download, FileText, Calendar, Video, Music,
  ShoppingCart, Youtube, Facebook, MessageCircle, Linkedin, Twitter, Instagram, Github,
} from 'lucide-react';

export const LINK_ICONS: Array<{ key: string; Icon: LucideIcon; label: string }> = [
  { key: 'link',      Icon: LinkIcon2,     label: 'Link'      },
  { key: 'download',  Icon: Download,       label: 'Download'  },
  { key: 'file',      Icon: FileText,       label: 'Datei'     },
  { key: 'calendar',  Icon: Calendar,       label: 'Kalender'  },
  { key: 'video',     Icon: Video,          label: 'Video'     },
  { key: 'music',     Icon: Music,          label: 'Musik'     },
  { key: 'shop',      Icon: ShoppingCart,   label: 'Shop'      },
  { key: 'youtube',   Icon: Youtube,        label: 'YouTube'   },
  { key: 'facebook',  Icon: Facebook,       label: 'Facebook'  },
  { key: 'whatsapp',  Icon: MessageCircle,  label: 'WhatsApp'  },
  { key: 'globe',     Icon: GlobeIcon,      label: 'Website'   },
  { key: 'linkedin',  Icon: Linkedin,      label: 'LinkedIn'  },
  { key: 'instagram', Icon: Instagram,     label: 'Instagram' },
  { key: 'twitter',   Icon: Twitter,        label: 'X / Twitter'},
  { key: 'github',    Icon: Github,         label: 'GitHub'    },
  { key: 'mail',      Icon: Mail,           label: 'E-Mail'    },
  { key: 'phone',     Icon: Phone,          label: 'Telefon'   },
  { key: 'mapPin',    Icon: MapPin,         label: 'Standort'  },
  { key: 'briefcase', Icon: Briefcase,      label: 'Job'       },
  { key: 'user',      Icon: User,           label: 'Person'    },
];

export const ICON_COMPONENT: Record<string, LucideIcon> = Object.fromEntries(
  LINK_ICONS.map(({ key, Icon }) => [key, Icon])
);

interface CustomLink {
  id: string;
  title: string;
  url: string;
  icon: string;
}

interface SortableLinkProps {
  link: CustomLink;
  onUpdate: (id: string, field: keyof CustomLink, value: string) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

function SortableLink({ link, onUpdate, onRemove, disabled }: SortableLinkProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: link.id, ...(disabled !== undefined ? { disabled } : {}) });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  const isImgIcon = link.icon.startsWith('http') || link.icon.startsWith('data:') || link.icon.startsWith('/');
  const [showImgInput, setShowImgInput] = React.useState(isImgIcon);
  const CurrentIcon = isImgIcon ? null : (ICON_COMPONENT[link.icon] ?? LinkIcon);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl bg-raised border border-line p-3 space-y-3 transition-all
                  ${isDragging ? 'opacity-60 shadow-2xl scale-[0.98] border-accent-500/30' : 'hover:border-line-strong'}`}
    >
      {/* Header row: grip + icon picker + remove */}
      <div className="flex items-center gap-2">
        {!disabled && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 text-ink-faint hover:text-ink cursor-grab active:cursor-grabbing touch-none transition-colors"
          >
            <GripIcon className="w-4 h-4" />
          </button>
        )}
        <div className="flex flex-wrap gap-1 flex-1">
          {LINK_ICONS.map(({ key, Icon }) => (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => { onUpdate(link.id, 'icon', key); setShowImgInput(false); }}
              title={key}
              className={`p-1.5 rounded-lg transition-all ${
                link.icon === key && !isImgIcon
                  ? 'bg-accent-600 text-white shadow-sm'
                  : 'bg-raised text-ink-faint hover:bg-line-strong hover:text-ink'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
          {/* Custom image icon button */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => { setShowImgInput((v) => !v); if (!showImgInput) onUpdate(link.id, 'icon', ''); }}
            title="Custom image"
            className={`p-1.5 rounded-lg transition-all text-xs font-bold ${
              isImgIcon
                ? 'bg-accent-600 text-white shadow-sm'
                : 'bg-raised text-ink-faint hover:bg-line-strong hover:text-ink'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            IMG
          </button>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onRemove(link.id)}
            className="flex-shrink-0 p-1.5 rounded-lg text-ink-faint hover:text-danger-400 hover:bg-danger-500/10 transition-all"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Custom image URL input */}
      {showImgInput && !disabled && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel border border-accent-500/20">
          {isImgIcon ? (
            <img src={link.icon} alt="" className="w-4 h-4 object-contain rounded flex-shrink-0" />
          ) : (
            <GlobeIcon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          )}
          <input
            type="url"
            value={isImgIcon ? link.icon : ''}
            onChange={(e) => onUpdate(link.id, 'icon', e.target.value)}
            placeholder="https://... (image URL for icon)"
            maxLength={500}
            className="w-full bg-transparent text-xs text-ink placeholder:text-ink-faint focus:outline-none"
          />
        </div>
      )}

      {/* Inputs row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel border border-line">
          {isImgIcon
            ? <img src={link.icon} alt="" className="w-3.5 h-3.5 object-contain rounded flex-shrink-0" />
            : CurrentIcon
              ? <CurrentIcon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
              : <LinkIcon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          }
          <input
            type="text"
            value={link.title}
            onChange={(e) => onUpdate(link.id, 'title', e.target.value)}
            placeholder="Label"
            disabled={disabled}
            maxLength={200}
            className="w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel border border-line">
          <GlobeIcon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
          <input
            type="url"
            value={link.url}
            onChange={(e) => onUpdate(link.id, 'url', e.target.value)}
            placeholder="https://..."
            disabled={disabled}
            maxLength={500}
            className="w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}

export default SortableLink;
